class OnlineService {
  constructor() {
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    // Each probe has its own isolated state object passed to _probe()
    this._backendProbe = {
      lastAt: 0,
      promise: null,
      result: null,
      cooldownMs: 15000,
      timeoutMs: 1500,
      url: null,
      fetchOptions: { method: 'HEAD' },
    };

    this._internetProbe = {
      lastAt: 0,
      promise: null,
      result: null,
      cooldownMs: 15000,
      timeoutMs: 2500,
      url:
        process.env.VUE_APP_CONNECTIVITY_CHECK_URL ||
        'https://clients3.google.com/generate_204',
      fetchOptions: { method: 'GET', mode: 'no-cors' },
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.isOnline = true;
      });
      window.addEventListener('offline', () => {
        this.isOnline = false;
        this._internetProbe.result = null;
      });
    }
  }

  /**
   * Generic probe: handles cooldown, in-flight deduplication, timeout and fetch.
   */
  _probe(state) {
    const now = Date.now();

    if (state.promise && now - state.lastAt < state.cooldownMs) {
      return state.promise;
    }

    if (now - state.lastAt < state.cooldownMs) {
      return state.result !== null ? state.result : this.isOnline;
    }

    state.lastAt = now;
    state.promise = new Promise((resolve) => {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), state.timeoutMs);

      fetch(state.url, {
        ...state.fetchOptions,
        cache: 'no-store',
        signal: controller.signal,
      })
        .then(() => {
          state.result = true;
          resolve(true);
        })
        .catch(() => {
          state.result = false;
          resolve(false);
        })
        .finally(() => {
          clearTimeout(tid);
          state.promise = null;
        });
    });

    return state.promise;
  }

  async probeConnectivity() {
    if (!this._backendProbe.url) {
      this._backendProbe.url = `${
        process.env.VUE_APP_API_ENDPOINT || window.location.origin
      }/dij`;
    }
    const result = await this._probe(this._backendProbe);
    this.isOnline = result;
    return result;
  }

  async probeInternetConnectivity() {
    return this._probe(this._internetProbe);
  }

  async getStatus() {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
    if (!this.isOnline) return this.probeConnectivity();
    return true;
  }

  async canConnectRealtime() {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.isOnline = false;
      return false;
    }
    return this.probeInternetConnectivity();
  }
}

export default new OnlineService();
